import { VisuallyHidden } from "@evidenceb/athena-common/ui/VisuallyHidden";
import range from "lodash/range";
import React, {
    CSSProperties,
    ChangeEventHandler,
    KeyboardEventHandler,
    useId,
    useRef,
    useState,
} from "react";
import { defineMessages, useIntl } from "react-intl";

import "./PinField.scss";

export type Props = {
    /**
     * Number of allowed fields
     * @default 4
     */
    fields?: number;
    name: string;
    onSubmit: ((value: string) => void) | ((value: string) => Promise<void>);
    isValid?: boolean;
    inputStyle: CSSProperties;
    inputStyleInvalid: CSSProperties;
};

const ALLOWED_CHARACTERS_REGEX = /[a-zA-Z0-9]/;

/**
 * Component to enter a pin code. Custom because libraries didn't satisfy a11y
 * or i18n needs
 */
const PinField = ({
    fields = 4,
    name,
    isValid = true,
    inputStyle,
    inputStyleInvalid,
    onSubmit,
}: Props) => {
    const intl = useIntl();
    const id = useId();

    const inputsRef = useRef<(HTMLInputElement | null)[]>(
        range(0, fields).map(() => null)
    );
    const [value, setValue] = useState<string[]>(
        range(0, fields).map(() => EMPTY)
    );
    const handleInputChange =
        (inputIndex: number): ChangeEventHandler<HTMLInputElement> =>
        (e) => {
            const nextChar = getNextInputValue(
                value[inputIndex],
                e.target.value
            );

            if (nextChar && !nextChar.match(ALLOWED_CHARACTERS_REGEX)) {
                const nextValue = [...value];
                nextValue[inputIndex] = EMPTY;
                setValue(nextValue);
                return;
            }

            const nextValue = [...value];
            nextValue[inputIndex] = nextChar;
            setValue(nextValue);

            if (!nextChar) {
                // Character was erased, do not do anything else
                return;
            }

            if (inputIndex === fields - 1) {
                // Auto validate when entering last input
                if (nextValue.every((char) => char !== EMPTY)) {
                    handleValidate(nextValue);
                }
            } else {
                // Auto go to next input
                const nextInput = inputsRef.current?.[inputIndex + 1];
                nextInput?.focus();
            }
        };
    const handleValidate = (value: string[]) => {
        const stringValue = makeStringValue(value);
        onSubmit(stringValue);
    };
    const handleKeyDown =
        (inputIndex: number): KeyboardEventHandler<HTMLInputElement> =>
        (e) => {
            // Go to previous input when hitting backspace on an empty input
            if (e.key !== "Backspace" || inputIndex === 0 || value[inputIndex])
                return;
            const previousInput = inputsRef.current?.[inputIndex - 1];
            previousInput?.focus();
        };

    return (
        <fieldset className="pinfield">
            <legend>
                <VisuallyHidden>{name}</VisuallyHidden>
            </legend>
            {range(0, fields).map((index) => (
                <div key={index}>
                    <VisuallyHidden>
                        <label htmlFor={makeInputId(id, index)}>
                            {intl.formatMessage(messages.characterXOfY, {
                                x: index + 1,
                                y: fields,
                            })}
                        </label>
                    </VisuallyHidden>
                    <input
                        ref={(elem) => (inputsRef.current[index] = elem)}
                        id={makeInputId(id, index)}
                        type="text"
                        style={{
                            ...inputStyle,
                            ...(isValid ? {} : inputStyleInvalid),
                        }}
                        value={value[index]}
                        onChange={handleInputChange(index)}
                        onKeyDown={handleKeyDown(index)}
                        autoCapitalize="characters"
                        required
                    />
                </div>
            ))}
        </fieldset>
    );
};
export default PinField;

const makeInputId = (mainId: string, index: number) => `${mainId}-${index}`;

const toUpperCase =
    <T,>(fn: (...args: T[]) => string): ((...args: T[]) => string) =>
    (...args) => {
        const ret = fn(...args);
        return ret.toUpperCase();
    };
const getNextInputValue = toUpperCase((prev: string, input: string) => {
    // Determine the new input based on the previous value to make sure we only
    // ever keep 1 character
    if (prev === EMPTY) {
        // Input was empty, take new value
        return input;
    } else if (input.length === 0 || !input) {
        // Input was erased, erase prev value
        return EMPTY;
    } else {
        // Input was not empty and new value was added : remove previous value and
        // take only new, wherever it was added in the input
        const next = input.replace(prev, "");
        if (next.length === 0) {
            // New char was same as before
            return prev;
        } else {
            return next;
        }
    }
});

const EMPTY = "";

const makeStringValue = (value: string[]): string => {
    return value.map((char) => char ?? " ").join("");
};

const messages = defineMessages({
    characterXOfY: {
        id: "pinField-characterXOfY",
        defaultMessage: "Character {x} of {y}",
    },
});
